// src/pages/Terms/TermsOfService.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import { seoConfig } from '../../config/seo';
import {
    FileText, Scale,
    Calendar, Mail, ShieldCheck,
    Briefcase, Ban, Copyright, Upload, AlertTriangle, Gavel, XCircle, Globe, Settings, Database
} from 'lucide-react';
import { Typography } from '../../components/ui/typography';

interface TOCItem {
    id: string;
    title: string;
}

const TermsOfService: React.FC = () => {
    const [activeSection, setActiveSection] = useState<string>('');

    const isScrollingRef = useRef(false);

    // Track active section for navigation
    useEffect(() => {
        const handleScroll = () => {
            if (isScrollingRef.current) return;

            const sections = document.querySelectorAll('section[id]');
            let current = '';

            sections.forEach((section) => {
                const rect = section.getBoundingClientRect();
                if (rect.top <= 160) {
                    const id = section.getAttribute('id');
                    if (id) current = id;
                }
            });

            setActiveSection(current);
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll to section
    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            setActiveSection(id);
            isScrollingRef.current = true;
            const offset = 20;
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
            setTimeout(() => { isScrollingRef.current = false; }, 1000);
        }
    };

    // Table of contents data
    const tableOfContents: TOCItem[] = [
        { id: 'agreement', title: 'Agreement to Terms' },
        { id: 'services', title: 'Our Services' },
        { id: 'acceptable-use', title: 'Acceptable Use' },
        { id: 'intellectual-property', title: 'Intellectual Property' },
        { id: 'user-content', title: 'Your Content' },
        { id: 'data-processing', title: 'Data Processing' },
        { id: 'disclaimers', title: 'Disclaimers' },
        { id: 'liability', title: 'Limitation of Liability' },
        { id: 'indemnification', title: 'Indemnification' },
        { id: 'third-party', title: 'Third-Party Services' },
        { id: 'termination', title: 'Termination' },
        { id: 'governing-law', title: 'Governing Law' },
        { id: 'miscellaneous', title: 'General Provisions' },
        { id: 'contact', title: 'Contact Information' }
    ];

    const lastUpdated = 'February 13, 2026';

    return (
        <>
            <SEO {...seoConfig.home} title="Terms of Service - FileFusion" description="Terms of Service for FileFusion. Read the terms governing your use of our file management, text processing, and developer tools." />

            <div className="min-h-screen bg-white font-brand text-[#202124]">

                {/* Header */}
                <header className="relative bg-white pt-16 pb-4 md:pt-24 md:pb-6 text-center overflow-hidden min-h-[280px] md:min-h-[320px]">
                    <div className="max-w-4xl mx-auto px-6 relative z-10 flex flex-col items-center">
                        <div className="min-h-[120px] md:min-h-[160px] flex flex-col justify-center">
                            <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-center max-w-4xl text-[#121317]">
                                Terms of Service
                            </Typography>
                            <Typography variant="hero-subtitle" weight="normal" className="mx-auto text-center max-w-2xl text-[#5f6368] mb-0">
                                Learn the terms governing your use of FileFusion's services
                            </Typography>
                        </div>
                        <div className="flex justify-center gap-4 mt-8">
                            <span className="inline-flex items-center gap-2 text-sm text-[#5f6368] bg-[#f8f9fa] px-4 py-1.5 rounded-full border border-[#dadce0] shadow-sm">
                                <Calendar size={16} />
                                Last updated: {lastUpdated}
                            </span>
                        </div>
                    </div>
                </header>

                {/* Main Container */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col lg:flex-row gap-12">

                    {/* Sidebar with Table of Contents */}
                    <aside className="hidden lg:block w-57 shrink-0">
                        <div className="sticky top-14 max-h-[calc(100vh-4rem)] overflow-y-auto">
                            <nav className="flex flex-col gap-1.5" aria-label="Table of contents">
                                {tableOfContents.map((item) => (
                                    <a
                                        key={item.id}
                                        href={`#${item.id}`}
                                        className={`block w-full px-3 py-2.5 text-sm rounded-md transition-colors cursor-pointer no-underline ${activeSection === item.id
                                            ? 'bg-[#e8f0fe] text-[#1a73e8] font-medium'
                                            : 'text-[#5f6368] hover:bg-[#f8f9fa] hover:text-[#202124]'
                                            }`}
                                        onClick={(e) => { e.preventDefault(); scrollToSection(item.id); }}
                                    >
                                        {item.title}
                                    </a>
                                ))}
                            </nav>

                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="flex-1 min-w-0">

                        {/* Agreement to Terms */}
                        <section id="agreement" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><Scale size={24} className="text-[#1a73e8] shrink-0" />Agreement to Terms</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                Welcome to FileFusion. These Terms of Service ("Terms") constitute a legally binding
                                agreement between you and FileFusion, operating from Mumbai, India, regarding
                                your access to and use of our website, tools, and services available at filefusion.app
                                (collectively, the "Services").
                            </Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                By accessing or using our Services, you represent and warrant that:
                            </Typography>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">You have the legal capacity to enter into these Terms</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">You are at least 16 years of age</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">If under 18, you have parental or guardian consent</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">You will comply with all applicable laws and regulations</span>
                                </li>
                            </ul>
                        </section>

                        {/* Our Services */}
                        <section id="services" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><Briefcase size={24} className="text-[#1a73e8] shrink-0" />Our Services</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                FileFusion provides free online tools for file management, text processing,
                                developer productivity, and educational content.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Core Tools</Typography>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Text Utilities:</strong> Text Comparison, JSON Validator, OCR Text Extractor</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">File Management:</strong> File Compressor, PDF Merge/Split</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Image Tools:</strong> AI Background Remover, Barcode/QR Scanner, QR Generator</span>
                                </li>
                            </ul>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Developer Hub</Typography>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Mock API:</strong> Prototyping REST API endpoints</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Webhook Tester:</strong> Real-time webhook inspection</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Simulators:</strong> Email Sandbox and Payment Simulator</span>
                                </li>
                            </ul>

                            <Typography variant="p" className="text-[#5f6368] leading-7 mt-6 mb-6">
                                <strong className="text-[#202124]">Developer Hub Disclaimer:</strong> The Payment Simulator and Email Sandbox are
                                <strong className="text-[#202124]"> simulation tools only</strong>. They do NOT process real financial transactions,
                                connect to real banking networks, or send actual emails to external recipients.
                                They are strictly for testing software integration.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Insights & Learning</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                We provide educational articles, technical guides, and courses ("Content"). This Content
                                is for informational purposes only. While we strive for accuracy, we make no guarantees
                                regarding the completeness or accuracy of any code snippets or technical advice.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Service Availability</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                We provide our Services on an "as is" and "as available" basis. While we strive for
                                uninterrupted service, we do not guarantee:
                            </Typography>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Continuous, error-free operation</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Freedom from bugs, viruses, or harmful components</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Specific uptime or availability levels</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">That all features will work on all devices or browsers</span>
                                </li>
                            </ul>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Service Modifications</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                We reserve the right to modify, suspend, or discontinue any aspect of our Services
                                at any time without prior notice. We are not liable for any modification, suspension,
                                or discontinuance of the Services.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">No Registration Required</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                Currently, FileFusion does not require account registration. You can use all tools
                                anonymously. If we introduce accounts in the future, we will update these Terms
                                accordingly.
                            </Typography>
                        </section>

                        {/* Acceptable Use */}
                        <section id="acceptable-use" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><Ban size={24} className="text-[#1a73e8] shrink-0" />Acceptable Use Policy</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">You agree to use our Services only for lawful purposes. You agree NOT to:</Typography>

                            <ul className="space-y-4">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Violate Laws:</strong> Use Services in violation of any applicable laws or regulations</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Harmful Content:</strong> Upload illegal, offensive, defamatory, or harmful content</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Malware:</strong> Upload viruses, malware, or technologically harmful material</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Unauthorized Access:</strong> Attempt to gain unauthorized access to our systems</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Automated Use:</strong> Use bots, scrapers, or automated tools without permission</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">IP Infringement:</strong> Process content that infringes intellectual property rights</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Interference:</strong> Disrupt or interfere with our Services or servers</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Impersonation:</strong> Impersonate FileFusion or other users</span>
                                </li>
                            </ul>

                            <Typography variant="p" className="text-[#5f6368] leading-7 mt-6">
                                Violation of this Acceptable Use Policy may result in immediate termination of your
                                access to our Services.
                            </Typography>
                        </section>

                        {/* Intellectual Property */}
                        <section id="intellectual-property" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><Copyright size={24} className="text-[#1a73e8] shrink-0" />Intellectual Property Rights</Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">FileFusion's IP</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                All content, features, and functionality of our Services, including but not limited to:
                            </Typography>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Text, graphics, logos, icons, images</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Software, source code, algorithms</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Design, user interface, "look and feel"</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Trademarks, service marks, trade names</span>
                                </li>
                            </ul>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mt-6 mb-6">
                                Are owned by FileFusion, our licensors, or other content providers, and are protected
                                by Indian and international intellectual property laws.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">License to Use</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                We grant you a limited, non-exclusive, non-transferable, non-sublicensable license to
                                access and use our Services for personal or internal business purposes. This license
                                does NOT include the right to:
                            </Typography>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Reproduce, distribute, or publicly display our Services</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Modify or create derivative works</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Reverse engineer, decompile, or disassemble our software</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Use our Services for commercial resale or redistribution</span>
                                </li>
                            </ul>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Trademarks</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                The FileFusion name, logo, and related marks are trademarks of FileFusion. You may not
                                use these marks without our prior written permission.
                            </Typography>
                        </section>

                        {/* Your Content */}
                        <section id="user-content" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><Upload size={24} className="text-[#1a73e8] shrink-0" />Your Content</Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Ownership</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                You retain all ownership rights to any content you upload, submit, or process through
                                our Services ("User Content"). We do not claim ownership of your User Content.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Your Responsibilities</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">You represent and warrant that:</Typography>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">You own or have necessary rights to your User Content</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Your User Content does not violate any third-party rights</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Your User Content complies with these Terms and all applicable laws</span>
                                </li>
                            </ul>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">How We Handle Your Content</Typography>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Secure File Handling:</strong> We use a combination of local and secure backend processing. Backend files are encrypted and never stored permanently.</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Automatic Deletion:</strong> Processed data is automatically deleted from our systems immediately after your session or task completion.</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">No Review:</strong> We do not review, monitor, or verify User Content (except where legally required).</span>
                                </li>
                            </ul>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Content Removal</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                We reserve the right (but have no obligation) to remove any User Content that violates
                                these Terms or is otherwise objectionable, without prior notice.
                            </Typography>
                        </section>

                        {/* Data Processing */}
                        <section id="data-processing" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><Database size={24} className="text-[#1a73e8] shrink-0" />Data Processing</Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">File Processing Risks</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                When you use our file processing tools (including but not limited to File Compressor, PDF Merge/Split,
                                AI Background Remover, OCR Text Extractor, and Text Comparison), you acknowledge and agree that:
                            </Typography>
                            <ul className="space-y-4 mb-6">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">File processing is performed at your own risk</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">We do not guarantee the accuracy, quality, or completeness of processed output</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">You are solely responsible for maintaining backups of your original files before processing</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">We are not liable for any data loss, corruption, or degradation that may occur during processing</span>
                                </li>
                            </ul>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Data Transmission</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                Certain tools require uploading files to our servers for processing. By using these tools, you
                                acknowledge that your files will be transmitted over the internet and processed on our servers.
                                While we use industry-standard encryption (SSL/TLS) during transmission, we cannot guarantee
                                absolute security of data in transit.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Local Processing</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                Some tools process files entirely within your browser (client-side). For these tools, your files
                                are never uploaded to our servers. However, we do not guarantee identical results across different
                                browsers, devices, or operating systems.
                            </Typography>
                        </section>

                        {/* Disclaimers */}
                        <section id="disclaimers" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><AlertTriangle size={24} className="text-[#1a73e8] shrink-0" />Disclaimers</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                <strong className="text-[#202124]">YOUR USE OF THE SERVICES IS AT YOUR OWN RISK.</strong> THE SERVICES ARE PROVIDED
                                "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
                                INCLUDING BUT NOT LIMITED TO:
                            </Typography>
                            <ul className="space-y-4 mb-6">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">IMPLIED WARRANTIES OF MERCHANTABILITY</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">FITNESS FOR A PARTICULAR PURPOSE</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">NON-INFRINGEMENT</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">ACCURACY, RELIABILITY, OR AVAILABILITY</span>
                                </li>
                            </ul>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE,
                                OR THAT DEFECTS WILL BE CORRECTED.
                            </Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                THE FOREGOING DOES NOT AFFECT ANY WARRANTIES THAT CANNOT BE EXCLUDED OR LIMITED UNDER
                                APPLICABLE LAW.
                            </Typography>
                        </section>

                        {/* Limitation of Liability */}
                        <section id="liability" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><Gavel size={24} className="text-[#1a73e8] shrink-0" />Limitation of Liability</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                <strong className="text-[#202124]">TO THE FULLEST EXTENT PERMITTED BY LAW:</strong>
                            </Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                FILEFUSION SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                                OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
                            </Typography>
                            <ul className="space-y-4 mb-6">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">LOSS OF PROFITS, REVENUE, OR BUSINESS</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">LOSS OF DATA OR CONTENT</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">BUSINESS INTERRUPTION</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">PERSONAL INJURY OR EMOTIONAL DISTRESS</span>
                                </li>
                            </ul>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                WHETHER CAUSED BY TORT (INCLUDING NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE,
                                EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                            </Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                IN JURISDICTIONS THAT DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR LIMITATIONS
                                OF LIABILITY, OUR LIABILITY WILL BE LIMITED TO THE MAXIMUM EXTENT PERMITTED BY LAW.
                            </Typography>
                        </section>

                        {/* Indemnification */}
                        <section id="indemnification" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><ShieldCheck size={24} className="text-[#1a73e8] shrink-0" />Indemnification</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                You agree to defend, indemnify, and hold harmless FileFusion, its officers, directors, employees,
                                agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities,
                                costs, and expenses (including reasonable attorneys' fees) arising from:
                            </Typography>
                            <ul className="space-y-4 mb-6">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Your use or misuse of the Services</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Your violation of these Terms</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Your violation of any applicable law or regulation</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Your infringement of any intellectual property or other right of any person or entity</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Any content you upload, process, or transmit through the Services</span>
                                </li>
                            </ul>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                This indemnification obligation will survive the termination of these Terms and your use of the Services.
                            </Typography>
                        </section>

                        {/* Third-Party Services */}
                        <section id="third-party" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><FileText size={24} className="text-[#1a73e8] shrink-0" />Third-Party Services & Links</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                Our Services may contain links to third-party websites or services not owned by FileFusion.
                                We have no control over and assume no responsibility for the content, privacy policies, or
                                practices of any third-party services.
                            </Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                We strongly advise you to read the terms and privacy policies of any third-party websites
                                or services you visit.
                            </Typography>
                        </section>

                        {/* Termination */}
                        <section id="termination" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><XCircle size={24} className="text-[#1a73e8] shrink-0" />Termination</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                We may terminate or suspend your access to our Services immediately, without prior notice
                                or liability, for any reason, including but not limited to:
                            </Typography>
                            <ul className="space-y-4 mb-6">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Violation of these Terms</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Fraudulent or illegal activity</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">Conduct harmful to other users or our Services</span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7">At our sole discretion for any other reason</span>
                                </li>
                            </ul>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                Upon termination, your right to use the Services will cease immediately. All provisions
                                of these Terms that by their nature should survive termination shall survive, including
                                ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                            </Typography>
                        </section>

                        {/* Governing Law */}
                        <section id="governing-law" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><Globe size={24} className="text-[#1a73e8] shrink-0" />Governing Law & Dispute Resolution</Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Governing Law</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                These Terms shall be governed by and construed in accordance with the laws of India,
                                without regard to conflict of law principles.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Jurisdiction</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                Any legal action or proceeding arising out of or relating to these Terms shall be
                                instituted exclusively in the courts of Mumbai, India.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Informal Resolution</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                Before filing any formal claim, you agree to first contact us at{' '}
                                <a href="mailto:contact@filefusion.app" className="text-[#1a73e8] hover:underline">contact@filefusion.app</a> and make a
                                good faith effort to resolve the dispute informally before initiating any formal proceedings.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Arbitration</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                If informal resolution fails, disputes will be resolved through binding arbitration
                                in accordance with the Arbitration and Conciliation Act, 1996 of India. The seat of
                                arbitration shall be Mumbai, India.
                            </Typography>
                        </section>

                        {/* General Provisions */}
                        <section id="miscellaneous" className="mb-20 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><Settings size={24} className="text-[#1a73e8] shrink-0" />General Provisions</Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Entire Agreement</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                These Terms, together with our Privacy Policy, constitute the entire agreement between
                                you and FileFusion regarding the Services.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Severability</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                If any provision of these Terms is found to be unenforceable, the remaining provisions
                                will continue in full force and effect.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Waiver</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                Our failure to enforce any right or provision of these Terms will not be deemed a waiver
                                of such right or provision.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Changes to Terms</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                We may revise these Terms at any time. The updated version will be indicated by an
                                updated "Last Updated" date. Continued use of the Services after changes constitutes
                                acceptance of the revised Terms.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Force Majeure</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                FileFusion shall not be liable for any failure or delay in performance resulting from causes
                                beyond our reasonable control, including but not limited to acts of God, natural disasters,
                                pandemics, war, terrorism, riots, government actions, power failures, internet or
                                telecommunications failures, fire, floods, or other catastrophic events.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Electronic Communications</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                By using our Services, you consent to receiving electronic communications from us. These
                                communications may include service announcements, administrative messages, and other
                                information related to the Services. You agree that any notices, agreements, disclosures,
                                or other communications we send electronically satisfy any legal communication requirements.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Feedback & Suggestions</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                If you provide feedback, suggestions, ideas, or recommendations regarding our Services
                                ("Feedback"), you grant FileFusion a non-exclusive, worldwide, royalty-free, irrevocable,
                                sub-licensable, perpetual license to use, reproduce, modify, and incorporate such Feedback
                                into our Services without any obligation of compensation or attribution to you.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Class Action Waiver</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                TO THE EXTENT PERMITTED BY APPLICABLE LAW, YOU AGREE THAT ANY DISPUTE RESOLUTION
                                PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS,
                                CONSOLIDATED, OR REPRESENTATIVE ACTION. YOU WAIVE THE RIGHT TO PARTICIPATE IN A
                                CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION AGAINST FILEFUSION.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Export Compliance</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-6">
                                You agree to comply with all applicable export and re-export control laws and regulations,
                                including the Indian Foreign Trade (Development & Regulation) Act, applicable US export
                                controls, and any other relevant international trade regulations. You shall not use the
                                Services in any country or region subject to comprehensive sanctions.
                            </Typography>

                            <Typography variant="h3" className="text-lg font-medium text-[#202124] mt-10 mb-5">Assignment</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                You may not assign or transfer these Terms, or any rights or obligations hereunder,
                                without our prior written consent. FileFusion may assign these Terms without restriction.
                                Any attempted assignment in violation of this section shall be null and void.
                            </Typography>
                        </section>

                        {/* Contact Information */}
                        <section id="contact" className="mb-16 scroll-mt-32">
                            <Typography variant="h2" className="text-2xl font-normal text-[#202124] mb-8 pb-4 border-b border-[#f1f3f4] flex items-center gap-3"><Mail size={24} className="text-[#1a73e8] shrink-0" />Contact Information</Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mb-8">
                                If you have questions about these Terms or need to report a violation, please contact us:
                            </Typography>
                            <ul className="space-y-4 mb-6">
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Email:</strong> <a href="mailto:contact@filefusion.app" className="text-[#1a73e8] hover:underline">contact@filefusion.app</a></span>
                                </li>
                                <li className="flex items-start gap-3 text-[#5f6368]">
                                    <span className="mt-2 w-2 h-2 bg-[#1a73e8] rounded-full shrink-0" />
                                    <span className="leading-7"><strong className="text-[#202124]">Address:</strong> FileFusion, Mumbai, India</span>
                                </li>
                            </ul>
                            <Typography variant="p" className="text-[#5f6368] leading-7">
                                We typically respond to inquiries within 48 hours during business days.
                            </Typography>
                        </section>

                        {/* Last Updated */}
                        <section className="mb-16">
                            <Typography variant="p" className="text-[#5f6368] leading-7">Last updated: <strong className="text-[#202124]">{lastUpdated}</strong></Typography>
                            <Typography variant="p" className="text-[#5f6368] leading-7 mt-4">
                                These Terms are effective as of the date stated above. By continuing to use FileFusion
                                after this date, you accept these Terms.
                            </Typography>
                        </section>

                        {/* Related Links */}
                        <div className="border-t border-[#f1f3f4] pt-10 mt-10">
                            <h3 className="text-sm font-semibold text-[#202124] mb-5">Related Information</h3>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/privacy" className="inline-flex items-center px-5 py-2.5 bg-[#f8f9fa] text-[#3c4043] rounded-full border border-[#dadce0] hover:bg-[#e8f0fe] transition-colors text-sm font-medium">
                                    Privacy Policy
                                </Link>
                                <Link to="/contact" className="inline-flex items-center px-5 py-2.5 bg-[#f8f9fa] text-[#3c4043] rounded-full border border-[#dadce0] hover:bg-[#e8f0fe] transition-colors text-sm font-medium">
                                    Contact Us
                                </Link>
                            </div>
                        </div>

                    </main>
                </div>
            </div>
        </>
    );
};

export default TermsOfService;
